import { useState, type ReactNode } from 'react';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import { Link, Navigate } from 'react-router-dom';
import Loading from '../../../../components/Loading';
import { ChatPanel } from '../../../../components/patient/ChatPanel';
import { PatientFilesTab } from '../../../../components/patient/PatientFilesTab';
import { usePatientAssets } from '../../../../hooks/usePatientAssets';
import { PatientChatProvider, useChatApiVersion } from '@odo/shared-ui';
import { getChatService } from '../../../../services/chatService';
import { usePatientMetadata } from '../../../../providers/PatientMetadataProvider';
import { TabSwitcher, type ApproveTab } from '../PatientApprove/TabSwitcher';

interface PatientActionPageProps {
    patientId: string | null;
    isLoading: boolean;
    patientName: string;
    /** Breadcrumb leaf label (e.g. "Approve case", "Deliver aligners"). */
    breadcrumbTitle: string;
    /** Prefix for the tabpanel id (e.g. "approve", "deliver"). */
    tabIdPrefix: string;
    /** Left column — the versioned document pane. */
    leftPane: ReactNode;
    /** Inner content of the "Case" tab (patient header + action-specific body). */
    caseContent: ReactNode;
    /** Pinned footer (action buttons), visible from every tab. */
    footer: ReactNode;
    /** Optional extra nodes (modals) rendered after the layout. */
    children?: ReactNode;
    /** Sends the GP to the overview when the action isn't theirs to take on this patient. */
    redirectToOverview?: boolean;
}

function Breadcrumb({ patientName, title }: { patientName: string; title: string }) {
    return (
        <div className="h-[72px] flex items-center pl-6 pr-20 flex-shrink-0">
            <nav className="flex items-center gap-1 text-xs text-gray-500" aria-label="Breadcrumb">
                <Link
                    to="/home"
                    className="hover:text-gray-800 transition-colors flex items-center"
                >
                    <HomeIcon className="w-3.5 h-3.5" />
                </Link>
                <ChevronRightIcon className="w-3 h-3 text-gray-300" />
                <Link to="/home" className="hover:text-gray-800 transition-colors">
                    Patients
                </Link>
                <ChevronRightIcon className="w-3 h-3 text-gray-300" />
                <span className="text-gray-700 font-semibold truncate max-w-[200px]">
                    {patientName || 'Patient'}
                </span>
                <ChevronRightIcon className="w-3 h-3 text-gray-300" />
                <span className="text-gray-900 font-semibold">{title}</span>
            </nav>
        </div>
    );
}

/**
 * Shared frame for the GP action pages (Approve / Deliver): breadcrumb, the
 * left document pane + right case/chat/files tabbed sidebar, the pinned footer,
 * and the PatientChatProvider wrapper. Pages provide the document pane, the
 * Case-tab content, the footer, and any modals.
 */
export function PatientActionPage({
    patientId,
    isLoading,
    patientName,
    breadcrumbTitle,
    tabIdPrefix,
    leftPane,
    caseContent,
    footer,
    children,
    redirectToOverview = false
}: PatientActionPageProps) {
    // Remounting on the version is not cosmetic: v1 ids are Monday update ids
    // and v2 ids are UUIDs, so the provider must rebuild rather than reconcile
    // its cached messages onto ids from the other world.
    const { version: chatApiVersion } = useChatApiVersion();
    const { hasUnreadMessages } = usePatientMetadata();
    const [tab, setTab] = useState<ApproveTab>('case');
    const chatUnread = patientId ? hasUnreadMessages(patientId) : false;

    // Patient assets for the Files tab — only fetched when that tab is open.
    const {
        assets,
        isLoading: isAssetsLoading,
        fetchError: assetsFetchError,
        refreshAssets
    } = usePatientAssets(patientId, { skip: tab !== 'files' });

    if (isLoading) return <Loading />;
    if (redirectToOverview && patientId) {
        return <Navigate to={`/patient/${patientId}/overview`} replace />;
    }

    const pageContent = (
        <div className="flex-1 min-h-0 flex flex-col">
            <Breadcrumb patientName={patientName} title={breadcrumbTitle} />

            <div className="flex-1 min-h-0 flex gap-5 px-6 py-5 overflow-hidden">
                <div className="flex-[0_0_59%] min-h-0 min-w-0">{leftPane}</div>

                <div className="flex-1 flex flex-col min-w-0 min-h-0">
                    <TabSwitcher active={tab} onChange={setTab} chatUnread={chatUnread} />

                    <div
                        className="flex-1 min-h-0 flex flex-col"
                        id={`${tabIdPrefix}-tab-${tab}`}
                        role="tabpanel"
                    >
                        {tab === 'case' && (
                            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide flex flex-col gap-3 pr-1">
                                {caseContent}
                            </div>
                        )}

                        {tab === 'chat' && patientId && (
                            <ChatPanel className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden" />
                        )}

                        {tab === 'files' && patientId && (
                            <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
                                <PatientFilesTab
                                    patientId={patientId}
                                    patientName={patientName}
                                    assets={assets}
                                    isLoading={isAssetsLoading}
                                    error={assetsFetchError}
                                    refreshAssets={refreshAssets}
                                />
                            </div>
                        )}
                    </div>

                    {footer}
                </div>
            </div>

            {children}
        </div>
    );

    return patientId ? (
        <PatientChatProvider
            patientId={patientId}
            key={chatApiVersion}
            service={getChatService(chatApiVersion)}
        >
            {pageContent}
        </PatientChatProvider>
    ) : (
        pageContent
    );
}
